import 'reflect-metadata'
import 'rxjs'
import 'zone.js'
import 'hammerjs'
import 'bootstrap'

import {
	provideHttpClient,
	withInterceptorsFromDi,
	HttpErrorResponse,
	HTTP_INTERCEPTORS,
} from '@angular/common/http'
import { appRoutingProviders, routing } from '@account/config/routing'
import {
	enableProdMode,
	ErrorHandler,
	importProvidersFrom,
	inject,
	provideAppInitializer,
	provideZoneChangeDetection,
} from '@angular/core'
import { PermissionsService } from '@account/Services/PermissionsService'
import { SystemConfigService } from '@account/Services/SystemConfigService'
import { CartService } from '@account/Services/CartService'
import * as Sentry from '@sentry/browser'
import { BrowserTracing } from '@sentry/tracing'
import { AuthenticatedHttpService } from '@account/Services/AuthenticatedHttpService'
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { AppService } from '@account/Services/AppService'
import { AccountService } from '@account/Services/AccountService'
import { OrderGuard } from '@account/Services/Guards/OrderGuard'
import { LoggedOutGuard } from '@account/Services/Guards/LoggedOutGuard'
import { LoggedInGuard } from '@account/Services/Guards/LoggedInGuard'
import { CartGuard } from '@account/Services/Guards/CartGuard'
import { ClassService } from '@account/Services/ClassService'
import { CategoryService } from '@account/Services/CategoryService'
import { AddressService } from '@account/Services/AddressService'
import { OrderService } from '@account/Services/OrderService'
import { PerformerService } from '@account/Services/PerformerService'
import { CheckoutGuard } from '@account/Services/Guards/CheckoutGuard'
import { OrderItemService } from '@account/Services/OrderItemService'
import { SchoolsService } from '@account/Services/SchoolsService'
import { WaitingListService } from '@account/Services/WaitingListService'
import { WaitingListGuard } from '@account/Services/Guards/WaitingListGuard'
import { PaymentMethodService } from '@account/Services/PaymentMethodService'
import { PasskeysService } from '@account/Services/PasskeysService'
import { WebAuthnService } from '@account/Services/WebAuthnService'
import { CommonModule } from '@angular/common'
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser'
import { provideAnimations } from '@angular/platform-browser/animations'
import { MatDialogModule } from '@angular/material/dialog'
import { MatSnackBarModule } from '@angular/material/snack-bar'
import { UiModule } from '@ui/config/module'
import { MatMenuModule } from '@angular/material/menu'
import { MatIconModule } from '@angular/material/icon'
import { MatBadgeModule } from '@angular/material/badge'
import { Angulartics2Module } from 'angulartics2'
import { AppComponent } from '@account/Components/App.Component'
import HttpError from '@ui/HttpError'
import AppErrorHandler from '@account/AppErrorHandler'

const HttpListener = {
	provide: HTTP_INTERCEPTORS,
	useClass: AuthenticatedHttpService,
	multi: true,
}
const BoundErrorhandler = {
	provide: ErrorHandler,
	useClass: AppErrorHandler,
}

if (process.env.NODE_ENV === 'production') {
	enableProdMode()
}

bootstrapApplication(AppComponent, {
	providers: [
		provideZoneChangeDetection(),
		importProvidersFrom(
			routing,
			CommonModule,
			BrowserModule,
			FormsModule,
			ReactiveFormsModule,
			MatDialogModule,
			MatSnackBarModule,
			UiModule,
			MatMenuModule,
			MatIconModule,
			MatBadgeModule,
			Angulartics2Module.forRoot(),
		),
		provideHttpClient(withInterceptorsFromDi()),
		appRoutingProviders,
		provideAppInitializer(async () => {
			const permissionService = inject(PermissionsService)
			const systemConfigService = inject(SystemConfigService)
			const cartService = inject(CartService)

			try {
				await permissionService.getPermissions()
				await systemConfigService.getConfig()
				if (
					permissionService.account &&
					systemConfigService.config.checkoutEnabled
				) {
					await cartService.getCart()
				}
				if (process.env.NODE_ENV === 'production') {
					Sentry.init({
						dsn: systemConfigService.config.sentryDsn,
						integrations: [new BrowserTracing()],
						release: systemConfigService.config.sentryRelease,
					})
				}
			} catch (error) {
				if (
					!(error instanceof HttpError) &&
					!(error instanceof HttpErrorResponse)
				) {
					throw error
				}
			}
		}),
		HttpListener,
		BoundErrorhandler,
		FormBuilder,
		PermissionsService,
		AppService,
		AccountService,
		SystemConfigService,
		CartService,
		OrderGuard,
		LoggedOutGuard,
		LoggedInGuard,
		CartGuard,
		ClassService,
		CategoryService,
		AddressService,
		OrderService,
		PerformerService,
		CheckoutGuard,
		OrderItemService,
		SchoolsService,
		WaitingListService,
		WaitingListGuard,
		PaymentMethodService,
		PasskeysService,
		WebAuthnService,
		provideAnimations(),
	],
}).catch((err) => {
	console.error(err)
})
